// YNX PWA Generator — client helpers
import JSZip from 'jszip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const generatePwa = async ({ name, shortName, description, themeColor, backgroundColor, iconPrompt, features, model = 'gpt-4o' }) => {
  const resp = await fetch(`${API}/pwa/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      short_name: shortName || null,
      description,
      theme_color: themeColor,
      background_color: backgroundColor,
      icon_prompt: iconPrompt || null,
      features: features || '',
      model,
    }),
  });
  if (!resp.ok) {
    const txt = await resp.text().catch(() => '');
    throw new Error(`PWA gen error ${resp.status}: ${txt.slice(0, 250)}`);
  }
  return resp.json();
};

/**
 * Resize a base64 PNG to a given square size using canvas.
 * Returns: Uint8Array (PNG bytes).
 */
export const resizeIconBase64 = async (b64, size) => {
  if (!b64) {
    // Build a solid square as a last-resort fallback
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#00ffff';
    ctx.font = `${Math.floor(size / 2)}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Y', size / 2, size / 2);
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
    return new Uint8Array(await blob.arrayBuffer());
  }
  const img = new Image();
  img.src = `data:image/png;base64,${b64}`;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  // Cover-fit (no distortion)
  const s = Math.min(img.width, img.height);
  const sx = (img.width - s) / 2;
  const sy = (img.height - s) / 2;
  ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);
  const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
  return new Uint8Array(await blob.arrayBuffer());
};

/**
 * Build the PWA zip from the backend payload and trigger a download.
 */
export const downloadPwaZip = async (payload) => {
  const zip = new JSZip();
  const slug = payload.slug || 'ynx-pwa';
  zip.file('index.html', payload.index_html);
  zip.file('manifest.json', payload.manifest_json);
  zip.file('sw.js', payload.service_worker_js);
  zip.file('README.md', payload.readme_md);
  const icon192 = await resizeIconBase64(payload.icon_b64, 192);
  const icon512 = await resizeIconBase64(payload.icon_b64, 512);
  zip.file('icon-192.png', icon192);
  zip.file('icon-512.png', icon512);
  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}-pwa.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};
